import { useEffect, useRef } from "react";
import { Users, Award } from "lucide-react";
import { doctors } from "../data";

export default function Doctors() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-up");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const branchColors = {
    mangalam: "bg-orange-100 text-orange-600 hover:bg-orange-200 hover:text-orange-700",
    koottayi: "bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700",
    vettom: "bg-teal-100 text-teal-600 hover:bg-teal-200 hover:text-teal-700",
  };

  // Card border colors for each doctor
  const doctorBorderColors = {
    1: "border-t-4 border-orange-400",
    2: "border-t-4 border-teal-400",
    3: "border-t-4 border-blue-600",
    4: "border-t-4 border-pink-400",
  };

  // Glow colors for each doctor
  const doctorGlowColors = {
    1: "from-orange-400 to-red-400",
    2: "from-teal-400 to-cyan-400",
    3: "from-blue-600 to-blue-800",
    4: "from-pink-400 to-rose-400",
  };

  // Ring colors for each doctor
  const doctorRingColors = {
    1: "ring-orange-500",
    2: "ring-teal-500",
    3: "ring-blue-600",
    4: "ring-pink-500",
  };

  // Name colors for each doctor
  const doctorNameColors = {
    1: "text-orange-600",
    2: "text-teal-600",
    3: "text-blue-700",
    4: "text-pink-600",
  };

  // Designation colors - matching doctor theme
  const doctorDesignationColors = {
    1: "text-orange-500",
    2: "text-teal-500",
    3: "text-blue-600",
    4: "text-pink-500",
  };

  return (
    <section ref={sectionRef} id="doctors" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-br from-white via-[#4a7c59]/5 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 animate-on-scroll opacity-0">
          <div className="inline-flex items-center px-4 py-2 bg-[#4a7c59]/10 rounded-full mb-4">
            <Users className="w-4 h-4 text-[#4a7c59] mr-2" />
            <span className="text-[#4a7c59] text-sm font-semibold">Our Team</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gray-800">Our Expert </span>
            <span className="text-gradient-olive">Doctors</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
            Meet our team of dedicated dental professionals committed to your smile
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {doctors.map((doctor, idx) => (
            <div
              key={doctor.id}
              className="animate-on-scroll opacity-0 group"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-500 transform hover:-translate-y-3 hover:border-[#4a7c59]/30 hover:shadow-[0_20px_45px_rgba(74,124,89,0.18)] h-full flex flex-col ${doctorBorderColors[doctor.id] || 'border-t-4 border-[#4a7c59]'}`}>
                
                {doctor.isChief && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                      Lead Dental Surgeon
                    </div>
                  </div>
                )}
                
                <div className="p-4 md:p-6 text-center flex-1 flex flex-col">
                  <div className="relative mb-4 flex justify-center">
                    <div className={`absolute inset-0 bg-gradient-to-r ${doctorGlowColors[doctor.id] || 'from-[#4a7c59] to-[#2d5a3f]'} rounded-xl blur-xl opacity-0 group-hover:opacity-40 transition-all duration-500`}></div>
                    
                    <div className={`relative w-32 h-36 md:w-28 md:h-32 rounded-xl overflow-hidden border border-gray-100 shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:ring-2 group-hover:ring-[#4a7c59]/20 ${doctor.isChief ? `ring-2 ${doctorRingColors[doctor.id] || 'ring-orange-500'}` : `ring-2 ${doctorRingColors[doctor.id] || 'ring-[#4a7c59]/30'}`}`}>
                      <img
                        src={doctor.image}
                        alt={doctor.imageAlt || doctor.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  <h3 className={`text-base md:text-lg font-bold mb-0.5 ${doctor.isChief ? 'text-orange-600' : doctorNameColors[doctor.id] || 'text-gray-800'}`}>
                    {doctor.name}
                  </h3>
                  
                  <p className={`text-xs md:text-sm font-medium ${doctorDesignationColors[doctor.id] || 'text-[#4a7c59]'}`}>
                    {doctor.designation}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{doctor.qualification}</p>

                  <div className="flex items-center justify-center gap-1 mt-2">
                    <Award className="w-3 h-3 text-[#4a7c59]" />
                    <span className="text-xs text-gray-600 font-medium">{doctor.experience} Experience</span>
                  </div>

                  {/* FIXED: Only show branches if doctor has any */}
                  {doctor.branches?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 font-medium mb-1.5">Available at:</p>
                      <div className="flex flex-wrap justify-center gap-1.5">
                        {doctor.branches.map((branch) => (
                          <span
                            key={branch}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-300 hover:scale-105 hover:shadow-md ${branchColors[branch] || 'bg-gray-100 text-gray-600'}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current inline-block mr-1.5"></span>
                            {branch.charAt(0).toUpperCase() + branch.slice(1)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center animate-on-scroll opacity-0">
          <p className="text-xs text-gray-500">
            Our experienced dental team is committed to providing safe, comfortable and personalized dental care across all Olive Dental Clinic branches.
          </p>
        </div>
      </div>
    </section>
  );
}